import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot } from '@angular/router';
import { AuthState } from 'src/app/store/auth';
import { UserInitService } from 'src/app/core/services/user-init.service';

/**
 * Auth Guard
 *
 * Protects routes by checking if the user is authenticated and has completed
 * user-initialization. If authenticated but init not done, redirects to /loading
 * so user-init can run (auth check -> load user doc -> setup-profile if none, else continue).
 * /loading and /setup-profile are always allowed so the init flow can run.
 *
 * @returns true if authenticated and initialized (or going to init pages), false with redirect
 */
export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authState = inject(AuthState);
  const router = inject(Router);
  const userInitService = inject(UserInitService);

  const navigation = router.getCurrentNavigation();
  const fullUrl = navigation?.finalUrl?.toString() || router.url;


  // Not authenticated -> sign-in
  const isAuthenticated = authState.isAuthenticated();
  if (!isAuthenticated) {
    router.navigate(['/sign-in'], {
      queryParams: { returnUrl: fullUrl || '/account' },
    });
    return false;
  }

  // Allow /loading and /setup-profile without init
  if (fullUrl.startsWith('/loading') || fullUrl.startsWith('/setup-profile')) {
    return true;
  }

  // Authenticated but init not complete -> go through /loading first
  if (!userInitService.initComplete) {
    router.navigate(['/loading'], {
      queryParams: { returnUrl: fullUrl === '/' ? '/my-blume' : fullUrl },
    });
    return false;
  }

  return true;
};

/**
 * Guest Guard
 *
 * Protects routes that should only be accessible to non-authenticated users.
 * If the user is authenticated, redirects to the loading page.
 *
 * @returns true if not authenticated, false otherwise (with redirect)
 */
export const guestGuard: CanActivateFn = route => {
  const authState = inject(AuthState);
  const router = inject(Router);


  const isAuthenticated = authState.isAuthenticated();

  if (isAuthenticated) {
    const returnUrl = route.queryParams['returnUrl'] || '/my-blume';
    router.navigate(['/loading'], {
      queryParams: { returnUrl },
      replaceUrl: true,
    });
    return false;
  }

  return true;
};
